import {
  setPwaServiceWorkerRegistration,
  setPwaRegistrationError,
} from "src/config/pwaRegistration";
import { APP_UPDATE_AVAILABLE_EVENT } from "src/utils/appUpdateEvents";

/** Stored `updateSW` so a user gesture (update banner) can apply the update. */
let updateSwRef = null;
let updateRequested = false;
let registrationPromise = null;
let serviceWorkerRegistration = null;
const checkedWorkers = new WeakSet();

// A waiting worker can belong to the build already loaded over the network,
// while an older worker still controls this tab. Waiting alone is not a deploy.
function isDifferentRelease(worker) {
  const assets = [...document.querySelectorAll('script[type="module"][src]')]
    .map((script) => script.src)
    .filter((src) => new URL(src).origin === window.location.origin);
  if (!assets.length) return Promise.resolve(false);
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const finish = (different) => {
      clearTimeout(timer);
      channel.port1.close();
      channel.port2.close();
      resolve(different);
    };
    // Legacy/unresponsive workers are not evidence of a newer release.
    const timer = setTimeout(() => finish(false), 2000);
    channel.port1.onmessage = (event) => finish(event.data?.sameRelease === false);
    try {
      worker.postMessage({ type: "CHECK_PAGE_ASSETS", assets }, [channel.port2]);
    } catch {
      finish(false);
    }
  });
}

/**
 * Apply the pending Service Worker update (skipWaiting + reload). Called from
 * WebUpdateBanner when the user explicitly accepts the new version.
 */
export function applyPwaUpdate() {
  updateRequested = true;
  if (updateSwRef) {
    updateSwRef(true);
  } else {
    window.location.reload();
  }
}

export async function registerPwaClient() {
  if (typeof window === "undefined") return;
  if (!registrationPromise) registrationPromise = registerPwaClientOnce();
  return registrationPromise;
}

async function registerPwaClientOnce() {
  // Dynamic import keeps the PWA code out of the main bundle; "virtual:" is
  // whitelisted in eslint.config.js (import/no-unresolved ignore).
  const { registerSW } = await import("virtual:pwa-register");

  // `registerSW` returns a function that can trigger `skipWaiting` + reload.
  const updateSW = registerSW({
    immediate: true,
    onNeedReload() {
      // The worker may already have been activated by another tab. In that
      // case a later click should reload, not message a nonexistent waiter.
      updateSwRef = null;
      // Accepting an update in one tab must not reload every other open form,
      // and a controller change triggered elsewhere (clientsClaim, another
      // tab's update, a stale worker finally activating) is NOT a new deploy —
      // other tabs keep their release and must not see the update banner.
      if (updateRequested) window.location.reload();
    },
    async onNeedRefresh() {
      // Don't reload silently — the user may have unsaved work in open forms.
      // Surface a controlled "new version" banner instead (WebUpdateBanner).
      const worker = serviceWorkerRegistration?.waiting;
      if (!worker || checkedWorkers.has(worker)) return;
      checkedWorkers.add(worker);
      if (!await isDifferentRelease(worker)) return;
      // Another tab may have activated it while the version check was pending.
      if (serviceWorkerRegistration.waiting !== worker) return;
      updateSwRef = updateSW;
      window.dispatchEvent(new CustomEvent(APP_UPDATE_AVAILABLE_EVENT));
    },
    onOfflineReady() {
      console.warn("[Doitify] App ready to work offline");
    },
    onRegistered(registration) {
      serviceWorkerRegistration = registration;
      console.warn("[Doitify] Service Worker registered");
      setPwaServiceWorkerRegistration(registration);
      setInterval(() => {
        // `update()` rejects with InvalidStateError when the registration has
        // no active worker (e.g. install failed or it was unregistered).
        // Only poll while the registration is actually valid, and never let a
        // rejection surface as an uncaught error.
        if (registration?.active) {
          registration.update().catch(() => {});
        }
      }, 60000);
    },
    onRegisterError(error) {
      setPwaRegistrationError(error);
      console.warn("[Doitify] Service Worker registration error:", error);
    },
  });

  if (import.meta.env.VITE_NODE_ENV === "production") {
    console.log = () => {};
    console.debug = () => {};
  }
}
